package com.anjuros.website.redesign.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.styles.RdCopyButtonStyle
import com.anjuros.website.redesign.util.RdIcons
import com.anjuros.website.redesign.util.RdText
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLTextAreaElement

private const val COPIED_FOR_MS = 2000L

/** A small button that copies [value] (an email address or phone number) and briefly shows "Copied". */
@Composable
fun RdCopyButton(value: String, label: RdText) {
    var copied by remember { mutableStateOf(false) }
    val labelText = LocalText(label)

    LaunchedEffect(copied) {
        if (copied) {
            delay(COPIED_FOR_MS)
            copied = false
        }
    }

    Button(RdCopyButtonStyle.toModifier().toAttrs {
        type(ButtonType.Button)
        attr("aria-label", labelText)
        attr("title", labelText)
        onClick {
            copyToClipboard(value)
            copied = true
        }
    }) {
        if (copied) {
            RdIcons.Check(size = 16)
            Span({ attr("aria-live", "polite") }) { Text(LocalText(RdText.COPY_DONE)) }
        } else {
            RdIcons.Copy(size = 16)
        }
    }
}

/** Uses the Clipboard API, falling back to a hidden text box for browsers that block it. */
private fun copyToClipboard(value: String) {
    val clipboard = window.navigator.asDynamic().clipboard
    if (clipboard != null) {
        (clipboard.writeText(value) as kotlin.js.Promise<Unit>).catch { copyWithTextArea(value) }
    } else {
        copyWithTextArea(value)
    }
}

private fun copyWithTextArea(value: String) {
    val area = document.createElement("textarea") as HTMLTextAreaElement
    area.value = value
    area.setAttribute("readonly", "")
    area.style.position = "fixed"
    area.style.opacity = "0"
    document.body?.appendChild(area)
    area.select()
    document.asDynamic().execCommand("copy")
    area.remove()
}
